type AnalyticsPrimitive = string | number | boolean;
type AnalyticsItem = Record<string, AnalyticsPrimitive>;
type AnalyticsParamValue = AnalyticsPrimitive | AnalyticsItem[];

const MAX_EVENT_NAME_LENGTH = 40;
const MAX_PARAM_NAME_LENGTH = 40;
const MAX_STRING_VALUE_LENGTH = 100;

function normalizeAnalyticsName(
  value: string,
  fallback: string,
  maxLength: number
) {
  const trimmed = value.trim().toLowerCase();
  const normalized = trimmed
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, maxLength);

  if (!normalized) {
    return fallback;
  }

  return /^[a-z]/.test(normalized) ? normalized : `x_${normalized}`;
}

function normalizeAnalyticsValue(
  value: unknown
): AnalyticsPrimitive | undefined {
  if (value == null) {
    return undefined;
  }

  if (typeof value === 'string') {
    return value.slice(0, MAX_STRING_VALUE_LENGTH);
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString().slice(0, MAX_STRING_VALUE_LENGTH);
  }

  try {
    return JSON.stringify(value).slice(0, MAX_STRING_VALUE_LENGTH);
  } catch {
    return String(value).slice(0, MAX_STRING_VALUE_LENGTH);
  }
}

function normalizeAnalyticsItems(value: unknown): AnalyticsItem[] | undefined {
  if (!Array.isArray(value)) return undefined;

  const items = value.flatMap((candidate) => {
    if (
      !candidate ||
      typeof candidate !== 'object' ||
      Array.isArray(candidate)
    ) {
      return [];
    }
    const item = Object.fromEntries(
      Object.entries(candidate).flatMap(([key, itemValue]) => {
        const normalizedValue = normalizeAnalyticsValue(itemValue);
        if (normalizedValue === undefined) return [];
        return [
          [
            normalizeAnalyticsName(key, 'x_param', MAX_PARAM_NAME_LENGTH),
            normalizedValue,
          ] as const,
        ];
      })
    );
    return Object.keys(item).length > 0 ? [item] : [];
  });

  return items.length > 0 ? items : undefined;
}

export function normalizeAnalyticsEventName(event: string) {
  return normalizeAnalyticsName(event, 'custom_event', MAX_EVENT_NAME_LENGTH);
}

export function normalizeAnalyticsEventParams(
  properties?: Record<string, unknown>,
  options?: { maxParameters?: number }
) {
  if (!properties) {
    return {};
  }

  const entries = Object.entries(properties);
  const normalizedEntries = entries.flatMap(([key, value]) => {
    const normalizedValue: AnalyticsParamValue | undefined =
      key === 'items'
        ? normalizeAnalyticsItems(value)
        : normalizeAnalyticsValue(value);
    if (normalizedValue === undefined) {
      return [];
    }

    return [
      [
        normalizeAnalyticsName(key, 'x_param', MAX_PARAM_NAME_LENGTH),
        normalizedValue,
      ] as const,
    ];
  });

  const maxParameters = options?.maxParameters;
  return Object.fromEntries(
    typeof maxParameters === 'number'
      ? normalizedEntries.slice(0, Math.max(0, maxParameters))
      : normalizedEntries
  );
}
